import { Component, Inject, Input, OnChanges, OnDestroy, SimpleChanges, DOCUMENT } from '@angular/core';
import { detect } from 'detect-browser';
import { Observable, Subscription } from 'rxjs';

import { ChangeUnitsComponent } from './change-units/change-units.component';
import { ModalService } from '../modal/modal.service';
import { ProjectApiService } from '../core/services/project-api.service';
import { SharedProjectService } from '../core/services/shared-project.service';
import { ProjectHistoryService } from '../core/services/project-history.service';
import { NewProjectComponent } from './new-project/new-project.component';
import { ProjectService } from '../core/services/project.service';
import { NavigationService } from '../core/services/navigation.service';
import { SaveProjectComponent } from './save-project/save-project.component';
import { SaveProjectService } from '../core/services/save-project.service';
import { UserApiService } from '../core/services/user-api.service';
import { ImportCsvComponent } from './import-csv/import-csv.component';
import { User } from '../core/objects/user';
import { UserService } from '../core/services/user.service';
import { ExportCsvComponent } from './export-csv/export-csv.component';
import { Project, VersionInfo } from '../core/objects/project';
import { ShareProjectComponent } from './share-project/share-project.component';
import { DownloadPdfComponent } from './download-pdf/download-pdf.component';
import { CookieService } from 'ngx-cookie-service';
import { ActivatedRoute, Router } from '@angular/router';

import { NewsNotificationsService } from '../core/services/news-notifications.service';
import { HEIGHT_ANIMATION } from '../shared/animations/animations';
import { LanguageService } from '../core/services/language.service';
import { MainView } from '../working-table/visualisation/main-view';
import { IntegrationApiService } from '../core/services/integration-api.service';
import {
  CompanyBranding,
  Group,
  IntegrationClient,
  PremiumApi,
  SharedProject,
  WorkSpace,
} from '../core/types';
import { Integration } from '../core/objects/Integration';
import { AuthorizationService } from '../authorization/services/authorization.service';
import { ColorsService } from '../core/services/colors.service';
import { Message, MessageService } from '../core/services/message.service';
import { ColorPipe } from '../shared/pipes/color.pipe';
import { WhiteLabelService } from '../core/services/white-label.service';
import { SharedChangesPopupComponent } from '../container/shared-changes-popup/shared-changes-popup.component';
import { TranslateService } from '@ngx-translate/core';
import { CompleteLocationDataPopupComponent } from '../container/complete-location-data/complete-location-data-popup.component';
import { SendToTransComponent } from './send-to-trans/send-to-trans.component';
import { take } from 'rxjs/operators';
import { convertProjectDimensions } from '../shared/helpers/unitRecalculationHelpers';

@Component({
    selector: 'gl-top-menu',
    templateUrl: './top-menu.component.html',
    styleUrls: ['./top-menu.component.scss'],
    animations: [HEIGHT_ANIMATION],
    standalone: false
})
export class TopMenuComponent implements OnDestroy, OnChanges {
  public unit: string;
  public isMobile: boolean;
  public isMagnetEnabled: boolean;
  public user: User;
  public userBadge: string;
  public userEmail: string;
  public project: Project;
  public pdfClicked: boolean;
  public isLoadNameEnabled: boolean;
  public isPalletContentsEnabled: boolean;
  public isBacklightEnabled: boolean;
  public isHintsEnabled: boolean;
  public isMeasureEnabled: boolean;
  public isShadowsEnabled: boolean;
  public isIlluminationLoadPlacesEnabled: boolean;
  public isOptimizationEnabled: boolean;
  public currentWorkSpaceIndex: number;
  private route: ActivatedRoute;
  public isFullscreen = false;
  public numberOfNotifications = 0;
  @Input() public workingTable: MainView;
  public premiumApiClient: IntegrationClient;
  public premiumApi: PremiumApi;
  public integrationData: Integration;
  public isSharedProject = false;
  public isSharedMenuOpen = false;
  public isMultiSpace = false;
  public currentGroup: Group;
  public currentObserver: number;
  public startedOrdedView = false;
  private subscription: Subscription;
  public currentStopIndex: number;
  public whiteLabel: CompanyBranding;
  public isShared = false;
  public currentProjectEditor: string;
  public currentVersion: number = null;
  public numberOfVersions: number;
  public currentProjectName: string;
  private projectVersions: VersionInfo[] = [];
  public currentReferenceId: string;
  public isUserLoggedIn = false;
  public isEmapa = false;
  public isTrans = false;

  constructor(
    private languageService: LanguageService,
    private router: Router,
    @Inject(DOCUMENT) private document: HTMLDocument,
    private newsNotifications: NewsNotificationsService,
    private cookieService: CookieService,
    private userService: UserService,
    private userApiService: UserApiService,
    private saveProjectService: SaveProjectService,
    private navigationService: NavigationService,
    private projectService: ProjectService,
    private projectHistoryService: ProjectHistoryService,
    private sharedProjectService: SharedProjectService,
    private modalService: ModalService,
    public projectApiService: ProjectApiService,
    private integrationApiService: IntegrationApiService,
    private authorizationService: AuthorizationService,
    private colorsService: ColorsService,
    private messageService: MessageService,
    public colorPipe: ColorPipe,
    private whiteLabelService: WhiteLabelService,
    private routeService: ActivatedRoute,
    private translate: TranslateService,
  ) {
    const browser = detect();
    this.isMobile =
      browser.os === 'iOS' ||
      browser.os === 'Android OS' ||
      browser.os === 'BlackBerry OS' ||
      browser.os === 'Windows Mobile' ||
      browser.os === 'Amazon OS';
    this.userService.user.subscribe((user: User) => {
      this.user = user;
      this.isUserLoggedIn = user ? Object.keys(user).length !== 0 : false;
      this.userEmail = user.email;
      if (user?.firstName) {
        this.userBadge = user.firstName[0];
      } else if (user?.email) {
        this.userBadge = user.email[0];
      } else {
        this.userBadge = 'u';
      }
    });
    this.subscription = this.messageService.getMessages().subscribe((message: Message): void => {
      if (!message) return;
      switch (message.name) {
        case 'startedOrderedViewOff':
          this.startedOrdedView = false;
          break;
        case 'currentObserver':
          this.currentObserver = message.data;
          break;
        case 'currentGroup':
          this.currentGroup = message.data;
          break;
        case 'savedProject':
          this.projectVersions = [];
          this.isShared = false;
          this.getProjectVersions();
          this.setProjectInfo();
          break;
      }
    });

    this.numberOfNotifications = this.newsNotifications.getState().newsNotifications
      ? this.newsNotifications.getState().newsNotifications.length
      : 0;
    if (this.isUserLoggedIn) {
      this.integrationApiService.getIntegration().subscribe((result: Integration) => {
        this.integrationData = result;
        this.premiumApiClient = this.integrationData
          ? this.integrationData.clients?.find((data) => data.isPremium)
          : undefined;
        if (this.premiumApiClient) {
          this.getPremiumApi();
        }
      });
    }
    this.isViewingSharedProject().subscribe((isShared) => {
      if (isShared) {
        this.sharedProjectService.sharedProject.subscribe((sharedProject: SharedProject): void => {
          this.projectService.setState(sharedProject.project);
          this.setSharedVersionInfo(sharedProject.versionInfo);
          this.project = sharedProject.project;
          this.currentProjectName = sharedProject.name;
          this.unit = sharedProject.project.options.unit;
        });
      } else {
        this.projectService.project.subscribe((project: Project): void => {
          const isProjectCompanyBranding: CompanyBranding = project?.companyBranding;
          this.whiteLabel = isProjectCompanyBranding
            ? project.companyBranding
            : this.whiteLabelService.getState();
          this.unit = project.options.unit;
          this.isMagnetEnabled = project.options.isMagnetOn;
          this.project = project;
          this.getProjectVersions();
          this.isEmapa = this.checkEmapa();
          this.setProjectInfo();
        });
      }
    });

    this.projectService.currentStopIndex.subscribe(
      (currentIndex) => (this.currentStopIndex = currentIndex),
    );
    this.projectService.currentWorkSpaceIndex.subscribe(
      (index) => (this.currentWorkSpaceIndex = index),
    );
    if (!this.cookieService.check('hints')) {
      this.cookieService.set('hints', 'true');
    }
    if (!this.cookieService.check('shadows')) {
      this.cookieService.set('shadows', 'true', undefined, '/', null, false, 'Lax');
    }
    this.isHintsEnabled = this.cookieService.get('hints').includes('true');
    this.isLoadNameEnabled = this.cookieService.get('loadName').includes('true');
    this.isPalletContentsEnabled = this.cookieService.get('palletContents').includes('true');
    this.isBacklightEnabled = this.cookieService.get('backlight').includes('true');
    this.isMeasureEnabled = this.cookieService.get('measure').includes('true');
    this.isOptimizationEnabled = true;
    this.isShadowsEnabled = this.cookieService.get('shadows').includes('true');
    this.isIlluminationLoadPlacesEnabled = this.cookieService
      .get('illuminationLoadPlaces')
      .includes('true');

    this.isViewingSharedProject().subscribe((sharedProject: boolean): void => {
      const isStop: number = this.routeService.snapshot.queryParams['stop'] as number;
      if (sharedProject && isStop) {
        const stopsNumber: number =
          this.project.workSpaces[this.currentWorkSpaceIndex].stops.length;
        this.changeStop(
          this.currentWorkSpaceIndex,
          isStop > 0 && isStop <= stopsNumber ? isStop - 1 : 0,
        );
      }
      this.isSharedProject = sharedProject;
      if (!sharedProject) {
        this.currentProjectName = this.projectApiService.currentProjectName;
      }
    });
    this.isTransUserLoggedIn();
  }

  private setProjectInfo(): void {
    if (this.isUserLoggedIn) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.projectApiService.getProjectInfo().subscribe((): void => {
        this.currentProjectName = this.projectApiService.currentProjectName;
      });
    }
  }

  private ResetSharedChangesPopups(): void {
    localStorage.removeItem('addLoadingSpace');
    localStorage.removeItem('addLoads');
  }

  public isToLong(text: string, allowedLetters: number): boolean {
    return text?.length > allowedLetters;
  }

  private isTransUserLoggedIn(): void {
    this.isTrans = this.authorizationService.getUserType() === 'trans';
  }

  private checkEmapa(): boolean {
    const isPlLanguage: boolean = this.translate.currentLang === 'pl';
    const isMultiStops: boolean = this.project.workSpaces[0]?.stops?.length > 1;
    return isPlLanguage && isMultiStops;
  }

  private getProjectVersions(): void {
    this.sharedProjectService.isViewingShared
      .pipe(take(1))
      .subscribe((isViewingShared: boolean): void => {
        if (isViewingShared) {
          this.sharedProjectService.sharedProject.subscribe(
            (sharedProject: SharedProject): void => {
              const versionInfo: VersionInfo[] = sharedProject?.versionInfo;
              this.currentProjectName = this.projectApiService.currentProjectName;
              if (versionInfo !== undefined && versionInfo !== null && versionInfo?.length > 0) {
                this.isShared = this.router.url.split('/').length - 1 < 2;
                this.projectVersions = [...this.mapProjectVersions(versionInfo)];
                this.isShared = this.projectVersions.length > 0;
                this.setSharedVersionInfo(versionInfo);
              }
            },
          );
        } else {
          this.projectApiService
            .getProjectVersions()
            .subscribe((versionInfo: VersionInfo[]): void => {
              if (versionInfo !== undefined && versionInfo !== null && versionInfo?.length > 0) {
                this.isShared = this.router.url.split('/').length - 1 < 2;
                this.projectVersions = [...this.mapProjectVersions(versionInfo)];
                this.isShared = this.projectVersions.length > 0;
                this.setVersionInfo();
              } else {
                this.isShared = false;
              }
            });
        }
      });
  }

  private setVersionInfo(): void {
    const currentVersionInfo: VersionInfo = this.projectVersions.find(
      (version: VersionInfo) => version.isCurrent,
    );
    if (currentVersionInfo) {
      this.currentProjectEditor =
        currentVersionInfo?.name === 'EX_USER'
          ? this.translate.instant('EX_USER')
          : currentVersionInfo?.name;
      this.currentVersion = currentVersionInfo.version;
      this.numberOfVersions = this.projectVersions.length;
      this.currentReferenceId = currentVersionInfo.referenceId;
    } else {
      this.isShared = false;
    }
  }

  private setSharedVersionInfo(versionInfo: VersionInfo[]): void {
    this.currentProjectName = this.projectApiService.currentProjectName;
    if (versionInfo !== undefined && versionInfo !== null && versionInfo?.length > 0) {
      this.isShared = this.router.url.split('/').length - 1 < 2;
      this.projectVersions = [...this.mapProjectVersions(versionInfo)];
      this.isShared = this.projectVersions.length > 0;
      this.setVersionInfo();
    }
  }

  public getColor(): string {
    return this.colorPipe.transform(this.currentGroup.color);
  }

  private changeCurrentVersion(fwd: boolean): void {
    if (fwd) {
      this.currentVersion++;
    } else {
      this.currentVersion--;
    }
  }

  public forwardVersionButton(): void {
    const findVersion: number = this.projectVersions.findIndex(
      (version: VersionInfo): boolean => version.referenceId === this.currentReferenceId,
    );
    const forwardReference: string = this.projectVersions[findVersion + 1]?.referenceId;
    this.projectService.setCurrentWorkSpace(0);
    this.isViewingSharedProject()
      .pipe(take(1))
      .subscribe((isViewingShared: boolean): void => {
        if (!isViewingShared) {
          this.projectApiService
            .editSharedProject(forwardReference, this.currentVersion + 1)
            .subscribe((): void => {
              this.changeCurrentVersion(true);
              this.getProjectVersions();
            });
        } else {
          this.projectApiService
            .getSharedProjectVersion(forwardReference, this.currentVersion + 1)
            .subscribe((result): void => {
              this.project = result.project;
              this.currentProjectName = this.projectApiService.currentProjectName;
              this.setSharedVersionInfo(result.versionInfo);
            });
        }
      });
  }

  public publishToTrans(): void {
    this.modalService.open(SendToTransComponent);
  }

  public backwardVersionButton(): void {
    this.projectService.setCurrentWorkSpace(0);
    this.isViewingSharedProject()
      .pipe(take(1))
      .subscribe((isViewingShared: boolean): void => {
        const backwardReference = this.projectVersions[0].referenceId;
        if (!isViewingShared) {
          this.projectApiService
            .editSharedProject(backwardReference, this.currentVersion - 1)
            .subscribe((): void => {
              this.changeCurrentVersion(false);
              this.getProjectVersions();
            });
        } else {
          this.projectApiService
            .getSharedProjectVersion(backwardReference, this.currentVersion - 1)
            .subscribe((result): void => {
              this.project = result.project;
              this.currentProjectName = this.projectApiService.currentProjectName;
              this.setSharedVersionInfo(result.versionInfo);
            });
        }
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public changeStop(workSpaceIndex: number, stopIndex: number): void {
    if (stopIndex < 0) return;
    if (this.project.workSpaces[this.currentWorkSpaceIndex].stops.length === stopIndex) return;
    this.projectService.setState(this.project, workSpaceIndex, stopIndex);
  }

  public getStopLetterFromIndex(stopIndex: number): string {
    return (stopIndex + 10).toString(36);
  }

  public leftMenuClick(): void {
    this.messageService.sendMessage({ name: 'leftMenuClick' });
  }

  public bottomMenuClick(): void {
    this.messageService.sendMessage({ name: 'bottomMenuClick' });
  }

  public closeSharedMenu(): void {
    this.isSharedMenuOpen = false;
  }

  public stopOrderView(): void {
    this.startedOrdedView = false;
    this.messageService.sendMessage({ name: 'stopOrderView' });
  }

  public startOrderView(): void {
    this.startedOrdedView = true;
    this.messageService.sendMessage({ name: 'startOrderView' });
  }

  public getPremiumApi(): void {
    this.integrationApiService
      .getPremiumApi(this.premiumApiClient.premiumApiId)
      .subscribe((premiumApi) => (this.premiumApi = premiumApi));
  }

  public nextOrderGroup() {
    this.messageService.sendMessage({ name: 'nextOrderGroup' });
  }

  public previousOrderGroup() {
    this.messageService.sendMessage({ name: 'previousOrderGroup' });
  }

  public getIconDataUrl(): string {
    return this.colorsService.colorIcons.get(this.currentGroup.color);
  }

  public logout(): void {
    this.authorizationService.logout().subscribe(() => {
      localStorage.removeItem('token');
      this.userService.setState(null);
      this.navigationService.navigateToLogin();
    });
  }

  public goHome(): void {
    this.isShared = this.projectVersions !== null && this.projectVersions.length > 0;
    this.navigationService.navigateToHomepage();
  }

  public openSharedMenu(): void {
    this.isMultiSpace = this.project.workSpaces[this.currentWorkSpaceIndex]?.stops
      ? this.project.workSpaces[this.currentWorkSpaceIndex].stops?.length > 0
      : false;
    this.isSharedMenuOpen = true;
  }

  public addShadows(): void {
    this.cookieService.get('shadows').includes('true')
      ? this.cookieService.set('shadows', 'false', undefined, '/', null, false, 'Lax')
      : this.cookieService.set('shadows', 'true', undefined, '/', null, false, 'Lax');
    this.isShadowsEnabled = !this.isShadowsEnabled;
    this.projectApiService.getProject().subscribe();
  }

  public addIlluminationLoadPlaces(): void {
    this.cookieService.get('illuminationLoadPlaces').includes('true')
      ? this.cookieService.set(
          'illuminationLoadPlaces',
          'false',
          undefined,
          '/',
          null,
          false,
          'Lax',
        )
      : this.cookieService.set(
          'illuminationLoadPlaces',
          'true',
          undefined,
          '/',
          null,
          false,
          'Lax',
        );
    this.isIlluminationLoadPlacesEnabled = !this.isIlluminationLoadPlacesEnabled;
  }

  public navigateToAccount(route: string): void {
    this.isShared = false;
    const defaultLanguage = this.languageService.getDefaultLanguage();
    this.router.navigateByUrl(`/${defaultLanguage}/account/${route}`);
  }

  public showLoadHints(): void {
    if (this.cookieService.get('hints').includes('true') && this.isHintsEnabled) {
      this.cookieService.set('hints', 'false', undefined, '/', null, false, 'Lax');
      this.cookieService.set('loadName', 'true', undefined, '/', null, false, 'Lax');
      this.isLoadNameEnabled = true;
    } else {
      this.cookieService.set('hints', 'true', undefined, '/', null, false, 'Lax');
      this.cookieService.set('loadName', 'false', undefined, '/', null, false, 'Lax');
      this.isLoadNameEnabled = false;
    }
    this.isHintsEnabled = !this.isHintsEnabled;
    this.projectApiService.getProject().subscribe();
  }

  public launchFullscreen(): void {
    if (!this.isFullscreen) {
      this.document.documentElement.requestFullscreen();
    } else {
      this.document.exitFullscreen();
    }
    this.isFullscreen = !this.isFullscreen;
  }

  public openNotifications(): void {
    this.router.navigate(['notifications'], {
      queryParamsHandling: 'preserve',
      relativeTo: this.route,
    });
  }

  public refresh(): void {
    this.isEmapa = this.checkEmapa();
  }

  public measureSwitch(): void {
    this.cookieService.get('measure').includes('true') && this.isMeasureEnabled
      ? this.cookieService.set('measure', 'false', undefined, '/', null, false, 'Lax')
      : this.cookieService.set('measure', 'true', undefined, '/', null, false, 'Lax');
    this.isMeasureEnabled = !this.isMeasureEnabled;
    this.projectApiService.getProject().subscribe();
  }

  public reset(): void {
    this.projectApiService.repack();
  }

  public deleteAll(): void {
    this.projectApiService.deleteAllGroups();
  }

  public shareProject(): void {
    this.modalService.open(ShareProjectComponent);
  }

  public backlightSwitch(): void {
    this.isBacklightEnabled = !this.isBacklightEnabled;
    this.cookieService.get('backlight').includes('true')
      ? this.cookieService.set('backlight', 'false', undefined, '/', null, false, 'Lax')
      : this.cookieService.set('backlight', 'true', undefined, '/', null, false, 'Lax');
    this.projectApiService.getProject().subscribe();
  }

  public switchMagnet(): void {
    const optionsUpdate = {
      currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpaceIndex].id,
      options: {
        isMagnetOn: !this.isMagnetEnabled,
      },
    };
    this.projectApiService.projectOptionsUpdate(optionsUpdate).subscribe();
  }

  public showLoadName(): void {
    if (this.cookieService.get('loadName').includes('true') && this.isLoadNameEnabled) {
      this.cookieService.set('loadName', 'false', undefined, '/', null, false, 'Lax');
      this.cookieService.set('hints', 'true', undefined, '/', null, false, 'Lax');
      this.isHintsEnabled = true;
    } else {
      this.cookieService.set('loadName', 'true', undefined, '/', null, false, 'Lax');
      this.cookieService.set('hints', 'false', undefined, '/', null, false, 'Lax');
      this.isHintsEnabled = false;
    }
    this.isLoadNameEnabled = !this.isLoadNameEnabled;
    this.projectApiService.getProject().subscribe();
  }

  public showPalletContents(): void {
    this.cookieService.get('palletContents').includes('true')
      ? this.cookieService.set('palletContents', 'false', undefined, '/', null, false, 'Lax')
      : this.cookieService.set('palletContents', 'true', undefined, '/', null, false, 'Lax');
    this.isPalletContentsEnabled = !this.isPalletContentsEnabled;
    this.projectApiService.getProject().subscribe();
  }

  public optimizationSwitch(): void {
    this.cookieService.get('optimization').includes('true') && this.isOptimizationEnabled
      ? this.cookieService.set('optimization', 'false', undefined, '/', null, false, 'Lax')
      : this.cookieService.set('optimization', 'true', undefined, '/', null, false, 'Lax');
    this.isOptimizationEnabled = !this.isOptimizationEnabled;
    this.projectApiService.getProject().subscribe();
  }

  public downloadPdf(withScreen: boolean): void {
    let projection: string = undefined;
    if (withScreen) {
      projection = this.workingTable.getImageData(true);
    }
    this.modalService.open(DownloadPdfComponent, projection);
  }

  public pdfClick(): void {
    this.pdfClicked = !this.pdfClicked;
  }

  public isCm(): boolean {
    return this.unit === 'cm';
  }

  public isMm(): boolean {
    return this.unit === 'mm';
  }

  public exportToCSV(): void {
    this.modalService.open(ExportCsvComponent);
  }

  public isViewingSharedProject(): Observable<boolean> {
    return this.sharedProjectService.isViewingShared;
  }

  public isWorkSpace(): boolean {
    return this.project && this.project.workSpaces && this.project.workSpaces.length > 0;
  }

  private openCompleteLocationDataPopup(): Observable<any> {
    return this.modalService.open(CompleteLocationDataPopupComponent);
  }

  private isProjectLastVersion(): boolean {
    if (this.projectVersions.length === 0) return false;
    const lastVersion: VersionInfo = this.projectVersions.reduce(
      (prev: VersionInfo, current: VersionInfo): VersionInfo =>
        prev.version > current.version ? prev : current,
    );
    return lastVersion.isCurrent;
  }

  private checkIfProjectHasValidAddresses(): boolean {
    const projectStops: WorkSpace[] = this.projectService.getState().workSpaces[0].stops;
    return projectStops.every((stop: WorkSpace): boolean => {
      return stop.country !== undefined && stop.city !== undefined;
    });
  }

  public connectWithEmapa(): void {
    const isAddressesValid: boolean = this.checkIfProjectHasValidAddresses();
    if (!isAddressesValid) {
      this.openCompleteLocationDataPopup().subscribe((data: string): void => {
        const isClose: boolean = data === 'close';
        if (isClose) {
          return;
        }
        this.projectApiService.getEmapaLink().subscribe((token): void => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const emapaLink = `https://webrouter.emapa.pl/Login/Goodloading?projectId=${token.id}`;
          window.open(emapaLink, '_blank');
        });
      });
    } else {
      this.projectApiService.getEmapaLink().subscribe((token): void => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const emapaLink = `https://webrouter.emapa.pl/Login/Goodloading?projectId=${token.id}`;
        window.open(emapaLink, '_blank');
      });
    }
  }

  private openLastVersionOfSharedProject(): void {
    const lastVersionReferenceId: string =
      this.projectVersions[this.projectVersions.length - 1].referenceId;
    const url = `session/?id=${lastVersionReferenceId}&pref=opt-names-opened`;
    setTimeout(() => {
      window.open(`${window.location.origin}/${this.translate.currentLang}/${url}`, '_blank');
    }, 1);
  }

  public saveProject(): void {
    const isProjects: boolean = this.projectVersions.length > 0;
    const sortedProjectVersions: VersionInfo[] = this.projectVersions.sort(
      (a: VersionInfo, b: VersionInfo) => b.version - a.version,
    );
    const lastVersionAuthor: string = isProjects ? sortedProjectVersions[0].name : '';
    const lastDate: string =
      isProjects && sortedProjectVersions[0].date ? sortedProjectVersions[0].date : '';
    const lastTime: string =
      isProjects && sortedProjectVersions[0].time ? sortedProjectVersions[0].time : '';
    if (this.projectApiService.currentProjectId && this.isProjectLastVersion()) {
      this.modalService.open(SaveProjectComponent).subscribe(() => this.getProjectVersions());
    } else if (this.isShared && !this.isProjectLastVersion()) {
      this.modalService
        .open(SharedChangesPopupComponent, `${lastVersionAuthor} (${lastDate} ${lastTime})`)
        .subscribe((result): void => {
          if (result.data === 'saveChanges') {
            if (this.projectApiService.currentProjectId) {
              const projectData = {
                name: this.projectApiService.currentProjectName,
                description: this.saveProjectService.generateProjectDescription(),
              };
              this.userApiService
                .saveProject(projectData)
                .subscribe(() => this.getProjectVersions());
            } else {
              this.modalService
                .open(SaveProjectComponent)
                .subscribe(() => this.getProjectVersions());
            }
          } else if (result.data === 'viewChanges') {
            this.openLastVersionOfSharedProject();
          }
        });
    } else {
      this.modalService.open(SaveProjectComponent).subscribe(() => this.getProjectVersions());
    }
  }

  public importFromCSV(): void {
    this.modalService.open(ImportCsvComponent).subscribe((result) => {
      if (result !== undefined) {
        this.isShared = false;
        this.projectVersions = [];
        this.ResetSharedChangesPopups();
      }
    });
  }

  public openProject(): void {
    this.messageService.sendMessage({ name: 'turnOffRecommendations' });
    this.messageService.sendMessage({ name: 'closeRecommendations' });
    this.navigationService.navigateToHomepage();
    this.navigationService.navigateToProjects();
    this.isShared = false;
    this.projectVersions = [];
    this.ResetSharedChangesPopups();
  }

  public isUndoImpossible(): boolean {
    return this.projectHistoryService.isUndoImpossible();
  }

  public newProject(): void {
    this.navigationService.navigateToHomepage();
    this.modalService.open(NewProjectComponent).subscribe((result) => {
      if (result === 'newProject') {
        this.projectVersions = [];
        this.isShared = false;
        this.ResetSharedChangesPopups();
      }
    });
  }

  public isProjectEmpty(): boolean {
    return this.projectService.isProjectEmpty();
  }

  public isRedoImpossible(): boolean {
    return this.projectHistoryService.isRedoImpossible();
  }

  public undo(): void {
    let projectForUndo: Project = this.projectHistoryService.getProjectForUndo();
    if (projectForUndo.isConverted) {
      projectForUndo.options.unit = 'cm';
      projectForUndo = convertProjectDimensions(projectForUndo, false);
      this.projectApiService.replaceProject(projectForUndo);
    } else {
      this.projectApiService.replaceProject(projectForUndo);
    }
  }

  public goToPro(): void {
    const languageCode = this.languageService.getDefaultLanguage();
    this.router.navigate([`/${languageCode}/account/subscription`]);
  }

  public redo(): void {
    let projectForRedo: Project = this.projectHistoryService.getProjectForRedo();
    if (projectForRedo.isConverted) {
      projectForRedo.options.unit = 'cm';
      projectForRedo = convertProjectDimensions(projectForRedo, false);
      this.projectApiService.replaceProject(projectForRedo);
    } else {
      this.projectApiService.replaceProject(projectForRedo);
    }
  }

  public changeUnits(event: MouseEvent, unit: string): void {
    event.stopPropagation();
    event.preventDefault();
    if (unit === 'mm') {
      this.modalService.open(ChangeUnitsComponent, unit).subscribe();
    } else if (this.unit !== unit) {
      this.modalService.open(ChangeUnitsComponent, unit).subscribe();
    }
  }

  private mapProjectVersions(versions: VersionInfo[]): VersionInfo[] {
    return versions.map((version: VersionInfo) => {
      const nameSplit: string[] = version.name.split(' ');
      const namex: string =
        nameSplit[0] !== 'null'
          ? nameSplit[0]
          : '' + nameSplit[1] !== 'null'
          ? nameSplit[1]
          : '' + nameSplit[2] !== 'null'
          ? nameSplit[2].replace(/[()]/g, '')
          : '';
      const date: Date = new Date(version.time);
      const year: number = date.getFullYear();
      const month: number = date.getMonth() + 1;
      const day: number = date.getDate();
      const hours: number = date.getHours();
      const minutes: number = date.getMinutes();
      const datex = `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`;
      const timex = `${hours < 10 ? '0' + hours : hours}:${minutes < 10 ? '0' + minutes : minutes}`;
      const newVersion: VersionInfo = {
        version: version.version,
        name: namex,
        date: datex,
        time: timex,
        referenceId: version.referenceId,
        isCurrent: version.isCurrent,
      };
      return newVersion;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.workingTable && changes.workingTable.currentValue) {
      if (!this.isViewingSharedProject) {
        this.getProjectVersions();
      }
    }
  }
}
